import { useEffect, useRef, useState } from 'react';
import { Link } from 'wouter';

import { ApiClientError } from '../../api/http';
import { Workspace, generateMcpKey, listWorkspaces, openWorkspace, workspaceMcpEndpoint } from '../../api/workspaces';
import { openFailureMessage, openWithWake, WAKING_MESSAGE } from '../Projects/WorkspacesPanel';

/** The text shown where the key goes until the person has generated one. */
export const KEY_PLACEHOLDER = 'YOUR_CONNECTION_KEY';

export interface ConnectSnippet {
  id: 'claude-code' | 'codex' | 'cursor' | 'claude-ai';
  title: string;
  steps: string;
  code: string;
}

/** Copy-paste setup for each tool. `key` is the real key only while it is on screen, else the placeholder. */
export function connectSnippets(endpoint: string, key: string = KEY_PLACEHOLDER): ConnectSnippet[] {
  return [
    {
      id: 'claude-code',
      title: 'Claude Code',
      steps: 'Run this once in your terminal. Claude Code then lists your studio as a connected tool.',
      code: `claude mcp add --transport http auteur-studio ${endpoint} --header "Authorization: Bearer ${key}"`,
    },
    {
      id: 'codex',
      title: 'Codex',
      steps: 'Put the key in an environment variable, then add this to ~/.codex/config.toml.',
      code: `export AUTEUR_STUDIO_KEY="${key}"\n\n[mcp_servers.auteur-studio]\nurl = "${endpoint}"\nbearer_token_env_var = "AUTEUR_STUDIO_KEY"`,
    },
    {
      id: 'cursor',
      title: 'Cursor',
      steps: 'Add this to ~/.cursor/mcp.json (or your project\'s .cursor/mcp.json), then reload Cursor.',
      code: JSON.stringify({
        mcpServers: { 'auteur-studio': { url: endpoint, headers: { Authorization: `Bearer ${key}` } } },
      }, null, 2),
    },
    {
      id: 'claude-ai',
      title: 'Claude Desktop and Claude.ai',
      steps: 'Claude.ai\'s own connector page asks for a sign-in flow that your studio does not offer yet. '
        + 'Claude Desktop can connect through a small bridge: add this to claude_desktop_config.json.',
      code: JSON.stringify({
        mcpServers: {
          'auteur-studio': {
            command: 'npx',
            args: ['-y', 'mcp-remote', endpoint, '--header', 'Authorization:${AUTEUR_STUDIO_AUTH}'],
            env: { AUTEUR_STUDIO_AUTH: `Bearer ${key}` },
          },
        },
      }, null, 2),
    },
  ];
}

/** A plain-language reason for a refused request. Never includes anything the server did not already say. */
export function connectFailureMessage(failure: unknown): string {
  if (failure instanceof ApiClientError) {
    if (failure.kind === 'timeout') return 'This took longer than expected. Your studio may have switched keys without showing you the new one, so create a new key to be sure.';
    if (failure.code === 'mcp_key_needs_attention') return 'Your studio is restarting and could not confirm your previous key yet. Wait a minute, then try again or contact support.';
    if (failure.status === 403) return 'Only the owner or an admin of this account can create a connection key.';
    if (failure.status === 409) return 'Your studio is not ready for this yet. Wait until it shows Running, then try again.';
    if (failure.status === 404) return 'We could not find your studio. Reload the page and try again.';
    if (failure.status === 401) return 'Please sign in again.';
    if (failure.status === 503) return 'Connection keys are not available right now. Your previous key still works. Try again shortly.';
  }
  return 'The connection key could not be created. Try again.';
}

export function connectLoadFailureMessage(failure: unknown): string {
  if (failure instanceof ApiClientError && failure.code === 'workspace_domain_unconfigured') {
    return 'Your studio is not set up yet. Set it up first, then come back here.';
  }
  return 'Your studio could not be loaded. Try again, or report a problem.';
}

export function shouldAutoWakeConnect(workspace: Workspace | null | undefined): workspace is Workspace {
  return workspace?.state === 'sleeping';
}

export function ConnectView({
  workspace, loadError, snippetsKey, issuedKey, confirming, busy, error, copied,
  waking = false, wakeError = '', onWake, onGenerate, onConfirm, onCancel, onHide, onCopy,
}: {
  workspace: Workspace | null | undefined;
  loadError: string;
  snippetsKey: string;
  issuedKey: string;
  confirming: boolean;
  busy: boolean;
  error: string;
  copied: boolean;
  waking?: boolean;
  wakeError?: string;
  onWake?: () => void;
  onGenerate: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  onHide: () => void;
  onCopy: () => void;
}) {
  if (loadError) return <p role="alert" className="error">{loadError}</p>;
  if (workspace === undefined) return <p role="status">Loading your studio…</p>;
  const usable = workspace !== null && (workspace.state === 'active' || workspace.state === 'sleeping');
  const endpoint = workspace ? workspaceMcpEndpoint(workspace.ingress_host) : '';
  return (
    <section className="connect-page">
      <header className="page-heading">
        <p className="eyebrow">Studio</p>
        <h1>Connect your AI tool</h1>
        <p>
          Use your own AI assistant (Claude Code, Codex, Cursor and others) to work with your studio, without
          learning the dashboard. You connect it once with a private connection key.
        </p>
        <p><Link to="/projects">Back to your studio</Link></p>
      </header>
      {workspace === null ? (
        <p className="empty-state">Your studio is not set up yet. Set it up first, then come back here.</p>
      ) : (
        <>
          {workspace.state === 'sleeping' ? (
            <section aria-labelledby="connect-wake-heading">
              <h2 id="connect-wake-heading">Your studio is asleep</h2>
              {waking ? <p role="status" aria-live="polite">{WAKING_MESSAGE}</p>
                : <p className="settings-muted">Wake your studio before connecting. We will open it as soon as it is ready.</p>}
              {wakeError ? <p role="alert" className="error">{wakeError}</p> : null}
              {onWake ? <button type="button" disabled={waking} onClick={onWake}>{waking ? 'Waking…' : wakeError ? 'Try again' : 'Wake and open studio'}</button> : null}
              {wakeError ? <p><Link to="/support">Report a problem</Link></p> : null}
            </section>
          ) : null}
          <section aria-labelledby="connect-key-heading">
            <h2 id="connect-key-heading">1. Your connection key</h2>
            <p className="settings-muted">
              The key is like a password for your AI tool. We show it once and do not keep a copy, so save it
              somewhere private. If it is lost or shared by mistake, make a new one: the old one stops working.
            </p>
            {issuedKey ? (
              <div role="status" className="connect-key">
                <p><strong>Your new connection key. Copy it now; it will not be shown again.</strong></p>
                <p className="settings-muted">It starts working in about a minute, once your studio has restarted. Until then your previous key still works. If your studio is asleep, open it first (use Open studio at the top); it does not wake by itself. If the key still does not work after a few minutes, create another.</p>
                <input readOnly aria-label="Connection key" value={issuedKey} onFocus={(event) => event.currentTarget.select()}
                  autoComplete="off" spellCheck={false} />
                <button type="button" onClick={onCopy}>{copied ? 'Copied' : 'Copy key'}</button>
                <button type="button" className="secondary" onClick={onHide}>I have saved it. Hide it.</button>
              </div>
            ) : confirming ? (
              <div role="group" aria-label="Confirm new connection key">
                <p>
                  Making a key replaces any earlier one, and your studio restarts for about a minute while it
                  switches over. Anything connected with the old key must be updated.
                </p>
                <button type="button" disabled={busy} onClick={onConfirm}>{busy ? 'Creating…' : 'Yes, create a new key'}</button>
                <button type="button" className="secondary" disabled={busy} onClick={onCancel}>Cancel</button>
              </div>
            ) : (
              <button type="button" disabled={!usable || busy} onClick={onGenerate}>Generate connection key</button>
            )}
            {!usable ? <p className="settings-muted">Your studio must be Running (or Sleeping) before you can create a key.</p> : null}
            {error ? <p role="alert" className="error">{error}</p> : null}
          </section>
          <section aria-labelledby="connect-tools-heading">
            <h2 id="connect-tools-heading">2. Connect your tool</h2>
            <p className="settings-muted">
              Your studio&apos;s address is <code>{endpoint}</code>. {issuedKey
                ? 'The snippets below already contain your new key.'
                : 'Replace YOUR_CONNECTION_KEY with your key.'}
            </p>
            {connectSnippets(endpoint, snippetsKey).map((snippet) => (
              <details key={snippet.id} className="connect-tool" open={snippet.id === 'claude-code'}>
                <summary>{snippet.title}</summary>
                <p>{snippet.steps}</p>
                <pre><code>{snippet.code}</code></pre>
              </details>
            ))}
            <p className="settings-muted">
              What your AI tool can do through this connection today is read-only: it can look around your
              studio and report back. Adding material to a project still happens inside the studio.
            </p>
          </section>
        </>
      )}
    </section>
  );
}

export function ConnectPage() {
  // undefined = loading, null = no usable studio yet.
  const [workspace, setWorkspace] = useState<Workspace | null | undefined>(undefined);
  const [loadError, setLoadError] = useState('');
  const [issuedKey, setIssuedKey] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [waking, setWaking] = useState(false);
  const [wakeError, setWakeError] = useState('');
  const alive = useRef(true);
  const autoWakeStarted = useRef(false);

  useEffect(() => {
    let active = true;
    alive.current = true;
    listWorkspaces()
      .then((rows) => {
        if (!active) return;
        const first = rows[0] ?? null;
        setWorkspace(first);
        if (shouldAutoWakeConnect(first) && !autoWakeStarted.current) {
          autoWakeStarted.current = true;
          void wakeStudio(first);
        }
      })
      .catch((failure: unknown) => {
        if (!active) return;
        if (failure instanceof ApiClientError && failure.code === 'workspace_domain_unconfigured') setWorkspace(null);
        else setLoadError(connectLoadFailureMessage(failure));
      });
    // The key lives only in this component's state: leaving the page drops it.
    return () => { active = false; alive.current = false; };
  }, []);

  async function confirm() {
    if (!workspace || busy) return;
    setBusy(true);
    setError('');
    try {
      setIssuedKey(await generateMcpKey(workspace.workspace_id));
      setConfirming(false);
      setCopied(false);
    } catch (failure) {
      setError(connectFailureMessage(failure));
    } finally {
      setBusy(false);
    }
  }

  async function wakeStudio(target: Workspace | null = workspace ?? null) {
    if (!target || waking) return;
    setWaking(true);
    setWakeError('');
    try {
      await openWithWake(target, {
        open: (row) => openWorkspace(row.workspace_id, row.ingress_host, undefined, () => alive.current),
        list: listWorkspaces,
        onWaking: () => { if (alive.current) setWaking(true); },
        wait: (ms) => new Promise((resolve) => globalThis.setTimeout(resolve, ms)),
        now: () => Date.now(),
      });
    } catch (failure) {
      if (alive.current) {
        setWakeError(openFailureMessage(failure));
        setWaking(false);
      }
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(issuedKey);
      setCopied(true);
    } catch {
      setError('Copying did not work. Select the key and copy it by hand.');
    }
  }

  return (
    <ConnectView workspace={workspace} loadError={loadError} snippetsKey={issuedKey || KEY_PLACEHOLDER}
      issuedKey={issuedKey} confirming={confirming} busy={busy} error={error} copied={copied}
      waking={waking} wakeError={wakeError} onWake={() => void wakeStudio(workspace)}
      onGenerate={() => { setError(''); setConfirming(true); }} onConfirm={() => void confirm()}
      onCancel={() => setConfirming(false)} onHide={() => { setIssuedKey(''); setCopied(false); }} onCopy={() => void copy()} />
  );
}
